import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import Select from 'react-select';
import type { MultiValue } from 'react-select';
import CorgeeStorageHelper from './CorgeeStorageHelper';
import { getVisibleLayers, getLayersName } from './ArrayUtil';
import * as apiHelper from './Utils';
import './ConversationLayers.css';
import { socketService } from '@/services/socketService';
import { getInlineSelectStyles } from './inlineSelectStyles';

// Types
interface Layer {
  LayerId: number;
  Name: string;
  LayerName?: string;
  LayerColor?: string;
  Color?: string;
  [key: string]: any;
}

interface GroupLayer {
  GroupId: number;
  GroupLayers: Layer[];
  [key: string]: any;
}

interface Conversation {
  ConversationId?: number;
  IsEnable?: boolean;
  ProjectId?: string | number;
  ConversationLayers?: Layer[];
  ConversationGroupLayers?: Layer[];
  GroupLayersValue?: string;
  selectedLayers?: number[];
  ConversationGroups?: GroupLayer[];
  PreviousLayers?: string;
  dashboardGroupId?: number;
  [key: string]: any;
}

interface ApiConfig {
  [key: string]: any;
}

interface ConversationGroupLayersProps {
  apiConfig: ApiConfig;
  itemType?: number;
  conversation: Conversation;
  disableAutoSave?: boolean;
  isNew?: boolean;
  onChangedGroupLayers?: (e: any, item: Conversation) => void;
}

export interface ConversationGroupLayersRef {
  onChangeParentLayers: (conversationLayers: Layer[]) => void;
  onChangeGroupsLayers: (groupsLayers: GroupLayer[], stopPropagation?: boolean) => void;
}

const ConversationGroupLayers = forwardRef<
  ConversationGroupLayersRef,
  ConversationGroupLayersProps
>(
  (
    { apiConfig, conversation, disableAutoSave = false, isNew = false, onChangedGroupLayers },
    ref,
  ) =>
  {
    const containerRef = useRef<HTMLDivElement>(null);

    const [canEdit, setCanEdit] = useState(false);
    const [editorEnabled, setEditorEnabled] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedNames, setSelectedNames] = useState<string>('Not Set');
    const [selectedLayers, setSelectedLayers] = useState<Layer[]>([]);
    const [originalSelectedIds, setOriginalSelectedIds] = useState<number[]>([]);
    const [dataGroupLayers, setDataGroupLayers] = useState<Layer[]>([]);

    const stateRef = useRef({ selectedLayers, selectedIds, originalSelectedIds, selectedNames });
    stateRef.current = { selectedLayers, selectedIds, originalSelectedIds, selectedNames };

    // Change parent layers
    const onChangeParentLayers = useCallback((conversationLayers: Layer[]) =>
    {
      if (Array.isArray(conversationLayers) && conversationLayers.length > 0)
      {
        const selectedValues = conversationLayers.map(item => item.LayerId);
        const names = getLayersName(conversationLayers);

        setSelectedIds(selectedValues);
        setSelectedNames(names);
        setOriginalSelectedIds(selectedValues);
      }
    }, []);

    // Change groups layers
    const onChangeGroupsLayers = useCallback(
      (groupsLayers: GroupLayer[], stopPropagation = false) =>
      {
        let selectedValues: number[] = [];
        let names: string = 'Not Set';
        const layers: Layer[] = [];
        let defaultLayer: Layer | undefined;

        const conversationCopy = { ...conversation };

        if (Array.isArray(groupsLayers))
        {
          groupsLayers.forEach(item =>
          {
            item.GroupLayers?.forEach(value =>
            {
              const exist = layers.some(x => x.LayerId === value.LayerId);
              if (!exist)
              {
                layers.push(value);
              }
            });
          });

          const finalDataLayers = getVisibleLayers(layers);
          setDataGroupLayers(finalDataLayers as any);

          const layerDefaultId = parseInt(CorgeeStorageHelper.layerId() || '0');

          if (layerDefaultId)
          {
            defaultLayer = (finalDataLayers as any[]).find(
              (item: Layer) => item.LayerId === layerDefaultId,
            );
          }
          if (!defaultLayer)
          {
            defaultLayer = (finalDataLayers as any[]).find((item: Layer) => item.LayerId === 1);
          }
          if (!defaultLayer)
          {
            defaultLayer = finalDataLayers[0] as Layer | undefined;
          }

          if (defaultLayer)
          {
            selectedValues = [defaultLayer.LayerId];
            names = defaultLayer.Name;
            conversationCopy.ConversationLayers = [defaultLayer];
          }
        }

        setSelectedIds(selectedValues);
        setSelectedNames(names);
        setOriginalSelectedIds(selectedValues);
        setSelectedLayers(defaultLayer ? [defaultLayer] : []);

        if (!stopPropagation && onChangedGroupLayers)
        {
          conversationCopy.ConversationGroups = groupsLayers || [];
          // Match what the picker shows: drop stale layers no selected group backs
          conversationCopy.ConversationLayers = defaultLayer ? [defaultLayer] : [];
          onChangedGroupLayers(undefined, conversationCopy);
        }
      },
      [conversation, onChangedGroupLayers],
    );

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        onChangeParentLayers,
        onChangeGroupsLayers,
      }),
      [onChangeParentLayers, onChangeGroupsLayers],
    );

    // Save changes
    const saveChanges = useCallback(
      (e: any, itemCopy: Conversation) =>
      {
        const { selectedLayers: sLayers, selectedIds: sIds, originalSelectedIds: oIds } =
          stateRef.current;
        const layersSelectedCopy = [...sLayers];

        // Never persist a card without layers
        if (!disableAutoSave && layersSelectedCopy.length > 0)
        {
          if (sIds.slice().sort().toString() !== oIds.slice().sort().toString())
          {
            itemCopy.PreviousLayers = oIds.join(', ');

            for (let index = 0; index < layersSelectedCopy.length; index++)
            {
              layersSelectedCopy[index]!.LayerName = layersSelectedCopy[index]!.Name;
              layersSelectedCopy[index]!.LayerColor =
                layersSelectedCopy[index]!.Color || layersSelectedCopy[index]!.LayerColor;
            }
            itemCopy.ConversationLayers = layersSelectedCopy;

            apiHelper
              .put(apiConfig as any, 'api/conversation/UpdateLayers', { ...itemCopy, SocketConnectionId: socketService.connectionId })
              .then((response: Layer[]) =>
              {
                const conversationLayers: Layer[] = [];
                if (itemCopy)
                {
                  for (let i = 0; i < response.length; i++)
                  {
                    conversationLayers.push({
                      ConversationId: itemCopy.ConversationId,
                      IsDefault: false,
                      LayerColor: response[i]!.LayerColor,
                      LayerId: response[i]!.LayerId,
                      LayerName: response[i]!.LayerName,
                      Name: response[i]!.LayerName || '',
                      Color: response[i]!.LayerColor,
                    });
                  }

                  itemCopy.ConversationLayers = conversationLayers;
                  itemCopy.PreviousLayers = sIds.join(', ');

                  if (onChangedGroupLayers)
                  {
                    onChangedGroupLayers(e, itemCopy);
                  }
                }
                setOriginalSelectedIds([...sIds]);
              })
              .catch(error =>
              {
                console.warn('Api Error', error);
              });
          }
        }
      },
      [disableAutoSave, apiConfig, onChangedGroupLayers],
    );

    const performSave = useCallback(() =>
    {
      const { selectedLayers: sLayers, selectedNames: sNames, selectedIds: sIds } =
        stateRef.current;

      const conversationCopy = { ...conversation };
      conversationCopy.ConversationGroupLayers = sLayers;
      conversationCopy.GroupLayersValue = sNames;
      conversationCopy.selectedLayers = [...sIds].reverse();
      conversationCopy.ConversationLayers = sLayers;

      if (isNew)
      {
        if (onChangedGroupLayers)
        {
          onChangedGroupLayers(undefined, conversationCopy);
        }
      }
      else
      {
        saveChanges(undefined, conversationCopy);
      }
    }, [conversation, isNew, onChangedGroupLayers, saveChanges]);

    // Select layers
    const onSelectGroupLayers = useCallback((itemsSelected: MultiValue<Layer>) =>
    {
      // An existing card must keep at least one layer (Legacy parity)
      if (!isNew && itemsSelected.length === 0)
      {
        return;
      }

      const selected = [...itemsSelected];
      setSelectedLayers(selected);
      setSelectedIds(selected.map(item => item.LayerId));
      setSelectedNames(getLayersName(selected));
    }, [isNew]);

    const onBlurSelect = useCallback(() =>
    {
      performSave();
      setEditorEnabled(false);
    }, [performSave]);

    // Enable editor
    const onEnableEditor = useCallback(() =>
    {
      if (isNew)
      {
        setEditorEnabled(true);
      }
      else
      {
        if (onChangedGroupLayers)
        {
          onChangedGroupLayers(undefined, conversation);
        }
      }
    }, [isNew, conversation, onChangedGroupLayers]);

    // Effect: Component mount
    useEffect(() =>
    {
      let canEditValidated = false;
      const isProject =
        conversation.ConfigValueId === 9 ||
        conversation.selectedConfigValueId === 9 ||
        window.location.href.indexOf('ProjectDetails') > -1;

      if (conversation.IsEnable !== undefined)
      {
        canEditValidated = CorgeeStorageHelper.canEdit() && conversation.IsEnable;
      }
      else
      {
        canEditValidated = CorgeeStorageHelper.canEdit() && (isNew || isProject);
      }

      let selectedValues: number[] = [];
      let names = 'Not Set';

      if (Array.isArray(conversation.ConversationLayers) && conversation.ProjectId)
      {
        selectedValues = conversation.ConversationLayers.map(item => item.LayerId);
        names = getLayersName(conversation.ConversationLayers);
      }

      setCanEdit(canEditValidated);
      setSelectedIds(selectedValues);
      setSelectedNames(names);
      setOriginalSelectedIds(selectedValues);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div className="click-to-edit" ref={containerRef}>
        {!canEdit && <div className="lbl-non-editable-input">{selectedNames}</div>}
        {canEdit &&
          (editorEnabled ? (
            <Select
              isMulti
              autoFocus
              openMenuOnFocus
              value={selectedLayers}
              options={dataGroupLayers}
              getOptionLabel={(o: Layer) => o.Name || o.LayerName || ''}
              getOptionValue={(o: Layer) => String(o.LayerId)}
              onChange={onSelectGroupLayers}
              onBlur={onBlurSelect}
              placeholder="Select a Layer..."
              isClearable={false}
              closeMenuOnSelect={true}
              styles={getInlineSelectStyles<Layer>()}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          ) : (
            <div className="lbl-editable-input project-changed-layers" onClick={onEnableEditor}>
              {selectedNames}
            </div>
          ))}
      </div>
    );
  },
);

ConversationGroupLayers.displayName = 'ConversationGroupLayers';

export default ConversationGroupLayers;
